import { Heading } from '@chakra-ui/layout'
import { Layout } from 'components/atoms'
import { InputBuild, SelectBuild } from 'components/molecules'
import { useContractContext } from 'contexts/contract'
import { LocationsDTO } from 'models'
import { disableUfFields } from 'pages/Contracts/utils/disableUfFields'
import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { getMunicipio } from 'services/municipio'
import isDisableToEditContract from '../../StepsForm/Utils/isDisableToEditContract'
import { FormFinancingValue } from './form'
import { FormFinancingProps } from './interfaces'
import { FormFinancingKeys } from './types'

const FormFinancing: React.FC = () => {
    const {
        register,
        watch,
        setValue,
        formState: { errors }
    } = useFormContext<{ financing: FormFinancingProps }>()

    const hasError = (id: FormFinancingKeys) => {
        return errors.financing && errors.financing[id]
    }

    const municipioLiberacaoCredito = 'financing.municipioLiberacaoCredito'
    const ufLiberacaoCredito = 'financing.ufLiberacaoCredito'

    const { typeContractBoolean, typeContract } = useContractContext()

    const [searching, setSearching] = useState(false)
    const [ufCompare, setUfCompare] = useState(watch(ufLiberacaoCredito))

    const [location, setLocation] = useState<LocationsDTO[]>([])

    const form = useMemo(
        () => FormFinancingValue(location, typeContract),
        [location]
    )

    const checkUfChange = () => {
        if (
            !watch(municipioLiberacaoCredito) ||
            ufCompare != watch(ufLiberacaoCredito)
        ) {
            setValue(municipioLiberacaoCredito, '')
            setUfCompare(watch(ufLiberacaoCredito))
        }
    }

    const consultMunicipio = async (uf: string) => {
        if (uf) {
            setSearching(true)
            return await getMunicipio(uf)
                .then(({ data }) => {
                    checkUfChange()
                    setLocation(data)
                })
                .finally(() => setSearching(false))
        } else {
            setLocation([])
        }
    }

    const getCurrentMunicipio = (municipio: string) => {
        if (location.length) {
            if (municipio.includes('|')) {
                return location.find(
                    item => item.municipio === municipio.split('|')[1]
                )
            }
            return location.find(item => item.municipio === municipio)
        }
    }

    const setMunicipio = () => {
        if (
            typeContract !== 'CREATE' &&
            typeContract !== 'EXTERNAL_ADDITIVE'
        ) {
            const municipioWatch = watch(municipioLiberacaoCredito)
            const municipio = getCurrentMunicipio(municipioWatch)!
            setValue(
                municipioLiberacaoCredito,
                `${municipio?.codigoTomDetran}|${municipio?.municipio}`
            )
        }
    }

    useEffect(() => {
        consultMunicipio(watch(ufLiberacaoCredito))
    }, [])

    useEffect(() => {
        if (location.length) {
            setMunicipio()
        }
    }, [location])

    useEffect(() => {
        const subscription = watch(async (_, { name }) => {
            if (name === ufLiberacaoCredito)
                consultMunicipio(watch(name)).finally(() => {
                    setMunicipio()
                })
        })
        return () => subscription.unsubscribe()
    }, [watch])

    return (
        <Layout
            type="container"
            px={8}
            full
            display="grid"
            gap={8}
            data-testid="form-container"
        >
            <Layout type="row" data-testid="form-heading">
                <Heading size="md" textAlign={{ sm: 'center' }}>
                    Dados do financiamento
                </Heading>
            </Layout>
            <Layout type="row" gutter={{ y: 1.5 }} data-testid="form-fields">
                {form.map(({ layout, input, select }, key) => (
                    <Layout {...layout} key={key}>
                        {select ? (
                            <SelectBuild
                                {...select}
                                err={hasError(select.id)}
                                {...register(`financing.${select.id}`)}
                                isSearching={select.isSearching && searching}
                                value={
                                    watch(`financing.${select.id}`) as string
                                }
                                isDisable={disableUfFields(
                                    typeContractBoolean,
                                    select,
                                    'financing'
                                )}
                            />
                        ) : (
                            input && (
                                <InputBuild
                                    {...input}
                                    err={hasError(input.id)}
                                    {...register(`financing.${input.id}`, {
                                        valueAsNumber: input.type === 'number'
                                    })}
                                    value={
                                        watch(`financing.${input.id}`) as string
                                    }
                                    isDisable={
                                        typeContractBoolean &&
                                        isDisableToEditContract[
                                            `financing.${input.id}`
                                        ]
                                    }
                                />
                            )
                        )}
                    </Layout>
                ))}
            </Layout>
        </Layout>
    )
}

export default FormFinancing
